import { useMemo } from 'react';

import type { SeasonModel } from '@snowit/types/seasons';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import { sortModel } from '@snowit/utilities/misc';
import omitter from '@snowit/utilities/omitter';

import useSchemas from '../useSchemas';

export default () => {
  const firebase = useFirebase();
  const { store, setStore } = useStore();
  const { writeDocument, deleteDocument } = useSchemas();

  const seasons = useMemo((): SeasonModel[] => {
    return store.seasons.list.sort(sortModel);
  }, [store.seasons.list]);

  const getSeasons = async (): Promise<SeasonModel[]> => {
    try {
      const res = await firebase.query<SeasonModel>({
        collectionName: 'seasons',
        filters: (q) => q.limit(0),
      });
      setStore('seasons', {
        list: res,
      });
      return res;
    } catch (error) {
      console.error({ error });
      return [];
    } finally {
      setStore('seasons', { ready: true });
    }
  };

  const writeSeason = async (
    uid: string | undefined,
    data: Partial<SeasonModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.SEASON,
        attributes: omitter(data),
        id: isNewEntity(uid) ? undefined : uid,
      });
      await getSeasons();
      return id;
    } catch (e: any) {
      console.error(e);
      return undefined;
    }
  };

  const deleteSeason = async (seasonUID: string): Promise<boolean> => {
    if (isNewEntity(seasonUID)) return false;
    try {
      await deleteDocument({
        modelName: SchemaNames.SEASON,
        id: seasonUID,
      });
      await getSeasons();
      return true;
    } catch (error: any) {
      console.error('deleteSeason', { error });
      return false;
    }
  };

  return {
    seasons,
    ready: store.seasons.ready,
    getSeasons,
    writeSeason,
    deleteSeason,
  };
};
